import tools from '@toolproof-core/genesis/_data/timestamped-resources/tools';
import type { ToolHandle } from '@toolproof-core/genesis/_contract/_index';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { WORKFLOW_KEYS, buildToolItemKey, parseRoleConstellationKey } from '@/workflow/_pure/keyCodec';
import { requireConfig } from '@/workflow-runner/_pure/config/config';
import { conceptInvocationSelection } from '@/workflow-runner/conceptToolInvocation';
import type { CosmosDataModel } from '@/workflow/_contract/cosmosData';
import type { Config } from '@/workflow/_contract/config';
import type { RuntimeState } from '@/workflow-runner/_contract/runtime';

const PRIMITIVE_HANDLES = tools.filter(({ projection: tool }) => !tool.isTemplate
    && [...Object.values(tool.roleSpec.inputRoleByName), ...Object.values(tool.roleSpec.outputRoleByName)]
        .every(role => role.resourceTypeHandle === 'TYPE-Natural' || role.resourceTypeHandle === 'TYPE-Boolean'))
    .map(({ projection }) => projection.handle)
    .filter((handle): handle is ToolHandle => handle.startsWith('TOOL-'));

export function conceptToolHandles(cosmosData: CosmosDataModel): ToolHandle[] {
    const handles = PRIMITIVE_HANDLES.filter(handle => cosmosData.toolMap.has(handle));
    if (!handles.length) throw new Error('Concept ToolTypes requires available primitive Natural or Boolean Tools.');
    return handles;
}

export function withConceptToolConfig(config: Config, cosmosData: CosmosDataModel): Config {
    return { ...config, toolSelection: { toolHandles: conceptToolHandles(cosmosData) } };
}

export function withConceptToolActivation(frame: SpaceDerivationFrame, state: RuntimeState, cosmosData: CosmosDataModel, config: Config): SpaceDerivationFrame {
    const handles = conceptToolHandles(cosmosData);
    const index = state.conceptTool && state.conceptTool.revision === state.episode?.revision ? state.conceptTool.index : 0;
    const invocation = state.conceptArrangement?.content === 'tool-invocation' ? conceptInvocationSelection(state, handles) : undefined;
    const handle = invocation ? invocation.handle : handles[index];
    if (!handle) throw new Error(`Concept ToolTypes has no Tool at index ${index}.`);
    if (!cosmosData.toolMap.has(handle)) throw new Error(`Concept Tool invocation requires ${handle}.`);
    const dimming = requireConfig(config).adapterModeConfig.nonActiveDimming;
    const entityConstellationByKey = { ...frame.constellationParametricState?.entityConstellationByKey };
    const toolState = entityConstellationByKey[WORKFLOW_KEYS.constellations.tool];
    if (toolState?.layout?.layoutKind !== 'sector-ring') throw new Error('Concept Tools require a sector-ring layout.');
    const pressed = invocation?.stage === 'invoked' || invocation?.stage === 'outputs';
    entityConstellationByKey[WORKFLOW_KEYS.constellations.tool] = { ...toolState,
        layout: { ...toolState.layout, activeThicknessScale: pressed ? toolState.layout.activeThicknessScale : 1 } };
    const connectorConstellationByKey = { ...frame.constellationParametricState?.connectorConstellationByKey };
    for (const constellation of frame.constellations) {
        if (constellation.constellationKind === 'entity') {
            const role = parseRoleConstellationKey(constellation.constellationKey);
            if (role) entityConstellationByKey[constellation.constellationKey] = {
                ...entityConstellationByKey[constellation.constellationKey],
                visibilityRule: undefined, visibility: { visible: role.toolHandle === handle },
            };
        } else if (constellation.meta?.ownerToolItemKey) {
            connectorConstellationByKey[constellation.constellationKey] = {
                ...connectorConstellationByKey[constellation.constellationKey],
                visibilityRule: undefined,
                visibility: { visible: constellation.meta.ownerToolItemKey === buildToolItemKey(handle) },
            };
        }
    }
    return { ...frame,
        constellationParametricState: { entityConstellationByKey, connectorConstellationByKey },
        spaceRenderOptions: { ...frame.spaceRenderOptions,
            activation: {
                activeItemKeyByConstellationKey: { [WORKFLOW_KEYS.constellations.tool]: buildToolItemKey(handle) },
                nonActiveItemDimming: {
                    ...dimming,
                    enabledByConstellationKey: { [WORKFLOW_KEYS.constellations.tool]: true },
                    enabledByLayoutKind: {},
                },
            },
        },
    };
}
